import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useNumbers } from '../context/NumberContext';

export default function Webhooks() {
  const { businesses, fetchAll } = useNumbers();
  const [selected, setSelected]     = useState(null); // selected biz id
  const [cfg, setCfg]               = useState({ forward_url:'', forward_url_2:'', forward_secret:'', forward_enabled:false });
  const [health, setHealth]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [testing, setTesting]       = useState('');
  const [testResult, setTestResult] = useState({});
  const [msg, setMsg]               = useState('');
  const [err, setErr]               = useState('');

  const flash = (m, isErr=false) => {
    isErr ? setErr(m) : setMsg(m);
    setTimeout(() => { setMsg(''); setErr(''); }, 4000);
  };

  // Auto-select first business
  useEffect(() => {
    if (businesses.length > 0 && !selected) setSelected(businesses[0].id);
  }, [businesses]);

  // Load forward config when selected changes
  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setHealth(null);
    setTestResult({});
    api.get(`/businesses/${selected}/webhook-forward`)
      .then(r => { setCfg(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selected]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/businesses/${selected}/webhook-forward`, cfg);
      flash('✅ Webhook forward settings saved!');
    } catch (e) { flash(e.response?.data?.error || 'Save failed', true); }
    finally { setSaving(false); }
  };

  const testUrl = async (urlKey) => {
    const url = cfg[urlKey];
    if (!url) return;
    setTesting(urlKey);
    setTestResult(p => ({ ...p, [urlKey]: null }));
    try {
      const r = await api.post(`/businesses/${selected}/webhook-forward/test`, { url });
      setTestResult(p => ({ ...p, [urlKey]: r.data }));
    } catch (e) {
      setTestResult(p => ({ ...p, [urlKey]: { success:false, error: e.response?.data?.error || e.message } }));
    }
    setTesting('');
  };

  const checkHealth = async () => {
    setHealthLoading(true);
    setHealth(null);
    try {
      const r = await api.get(`/businesses/${selected}/webhook-health`);
      setHealth(r.data);
    } catch (e) { setHealth({ error: e.response?.data?.error || e.message }); }
    setHealthLoading(false);
  };

  const fixSubscription = async () => {
    setHealthLoading(true);
    try {
      await api.post(`/businesses/${selected}/webhook-subscribe`);
      flash('✅ WABA subscribed! Webhooks are now active.');
      await checkHealth();
    } catch (e) { flash(e.response?.data?.error || 'Subscribe failed — check token permissions', true); }
    setHealthLoading(false);
  };

  const genSecret = () => {
    const s = 'whsec_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2,'0')).join('');
    setCfg(p => ({ ...p, forward_secret: s }));
  };

  const biz = businesses.find(b => b.id === selected);
  const webhookUrl = `${window.location.origin}/api/webhook`;

  return (
    <div style={{ padding:'20px 16px', maxWidth:780, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <h2 style={{ fontFamily:'var(--mono)', fontSize:13, letterSpacing:'0.06em', color:'var(--text2)', margin:0 }}>
          🔗 WEBHOOKS
        </h2>
        <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>
          Manage webhook health, subscriptions, and forward incoming data to external APIs
        </div>
      </div>

      {msg && <div style={fStyle('ok')}>{msg}</div>}
      {err && <div style={fStyle('err')}>{err}</div>}

      {businesses.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🏢</div>
          <div style={{ fontSize:14 }}>No businesses added yet. Add a business first.</div>
        </div>
      ) : (
        <>
          {/* Business selector */}
          <div style={{ marginBottom:20 }}>
            <label className="label">Select Business</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {businesses.map(b => (
                <button key={b.id} onClick={() => setSelected(b.id)}
                  className={selected === b.id ? 'btn-primary' : 'btn-ghost'}
                  style={{ fontSize:12 }}>
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          {biz && (
            <>
              {/* Webhook URL */}
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--accent)', marginBottom:10 }}>
                  // META WEBHOOK CONFIGURATION
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:6 }}>
                  <span style={{ fontSize:12, color:'var(--text2)', fontWeight:500 }}>Callback URL:</span>
                  <code style={{ fontSize:12, color:'var(--accent)', wordBreak:'break-all' }}>{webhookUrl}</code>
                  <button onClick={() => { navigator.clipboard.writeText(webhookUrl); flash('Copied!'); }}
                    className="btn-ghost" style={{ fontSize:11, padding:'2px 8px' }}>Copy</button>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:12, color:'var(--text2)', fontWeight:500 }}>Verify Token:</span>
                  <code style={{ fontSize:12, color:'var(--accent2)' }}>{biz.webhook_verify_token}</code>
                  <button onClick={() => { navigator.clipboard.writeText(biz.webhook_verify_token); flash('Copied!'); }}
                    className="btn-ghost" style={{ fontSize:11, padding:'2px 8px' }}>Copy</button>
                </div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>
                  Set these in Meta Developer Console → Your App → WhatsApp → Configuration → Webhook
                </div>
              </div>

              {/* WABA Health */}
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--accent)', marginBottom:8 }}>
                  // WABA SUBSCRIPTION HEALTH
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginBottom:12, lineHeight:1.6 }}>
                  Meta requires your WABA to be subscribed to your app for webhooks to actually deliver messages.
                  Many setups fail silently here — verify and fix with one click.
                </div>

                {health?.error && <div style={{ ...fStyle('err'), marginBottom:10 }}>{health.error}</div>}

                {health && !health.error && (
                  <div style={{ padding:'10px 14px', borderRadius:'var(--r-sm)', marginBottom:12,
                    background: health.subscribed ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                    border:`1px solid ${health.subscribed ? '#10b981' : 'var(--danger)'}` }}>
                    <div style={{ fontWeight:600, fontSize:13, color: health.subscribed ? '#10b981' : 'var(--danger)', marginBottom:4 }}>
                      {health.subscribed ? '✅ Subscribed — webhooks are active' : '❌ NOT Subscribed — messages will NOT be delivered!'}
                    </div>
                    {health.subscribed && health.apps?.length > 0 && (
                      <div style={{ fontSize:11, color:'var(--text3)' }}>
                        App: {health.apps[0]?.name || health.apps[0]?.id} · Checked: {new Date(health.checked_at).toLocaleTimeString()}
                      </div>
                    )}
                    {!health.subscribed && (
                      <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>
                        Click "⚡ Fix Now" below to subscribe your WABA to the app.
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button onClick={checkHealth} disabled={healthLoading} className="btn-ghost" style={{ fontSize:12 }}>
                    {healthLoading ? '⏳ Checking…' : '🔍 Check Status'}
                  </button>
                  {health && !health.subscribed && !health.error && (
                    <button onClick={fixSubscription} disabled={healthLoading} className="btn-primary" style={{ fontSize:12 }}>
                      ⚡ Fix Now — Subscribe WABA
                    </button>
                  )}
                </div>
              </div>

              {/* Forward URLs */}
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--accent)', marginBottom:4 }}>
                  // WEBHOOK FORWARD URLS
                </div>
                <div style={{ fontSize:12, color:'var(--text3)', marginBottom:16, lineHeight:1.6 }}>
                  Forward all incoming WhatsApp messages and status events to your own API endpoint, n8n, Zapier, Make, or any HTTP webhook.
                </div>

                {loading ? <div style={{ color:'var(--text3)', fontSize:12 }}>Loading…</div> : (
                  <>
                    {/* Enable toggle */}
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16,
                      padding:'10px 14px', background:'var(--bg3)', borderRadius:'var(--r-sm)',
                      border:'1px solid var(--border)' }}>
                      <input type="checkbox" id="fw-enabled" checked={cfg.forward_enabled}
                        onChange={e => setCfg(p => ({ ...p, forward_enabled: e.target.checked }))}
                        style={{ width:16, height:16, cursor:'pointer' }} />
                      <label htmlFor="fw-enabled" style={{ cursor:'pointer', fontSize:13, fontWeight:500 }}>
                        Enable webhook forwarding for <strong>{biz.label}</strong>
                      </label>
                    </div>

                    {/* Primary URL */}
                    <div style={{ marginBottom:16 }}>
                      <label className="label">Primary Forward URL</label>
                      <div style={{ display:'flex', gap:8 }}>
                        <input type="url" value={cfg.forward_url} placeholder="https://your-n8n.com/webhook/abc123"
                          onChange={e => setCfg(p => ({ ...p, forward_url: e.target.value }))}
                          style={{ flex:1, fontSize:12, fontFamily:'var(--mono)' }} />
                        <button onClick={() => testUrl('forward_url')}
                          disabled={testing === 'forward_url' || !cfg.forward_url}
                          className="btn-ghost" style={{ fontSize:12, whiteSpace:'nowrap' }}>
                          {testing === 'forward_url' ? '⏳' : '🧪 Test'}
                        </button>
                      </div>
                      {testResult.forward_url && (
                        <div style={{ marginTop:6, fontSize:11, padding:'6px 10px', borderRadius:6,
                          background: testResult.forward_url.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                          border:`1px solid ${testResult.forward_url.success ? '#10b981' : 'var(--danger)'}`,
                          color: testResult.forward_url.success ? '#10b981' : 'var(--danger)' }}>
                          {testResult.forward_url.success
                            ? `✅ Success — HTTP ${testResult.forward_url.status} in ${testResult.forward_url.ms}ms`
                            : `❌ Failed — ${testResult.forward_url.error}`}
                        </div>
                      )}
                      <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                        All inbound messages and delivery status events are POSTed here in real-time
                      </div>
                    </div>

                    {/* Secondary URL */}
                    <div style={{ marginBottom:16 }}>
                      <label className="label">Secondary Forward URL <span style={{ fontWeight:400, color:'var(--text3)' }}>(optional)</span></label>
                      <div style={{ display:'flex', gap:8 }}>
                        <input type="url" value={cfg.forward_url_2} placeholder="https://hooks.zapier.com/hooks/catch/..."
                          onChange={e => setCfg(p => ({ ...p, forward_url_2: e.target.value }))}
                          style={{ flex:1, fontSize:12, fontFamily:'var(--mono)' }} />
                        <button onClick={() => testUrl('forward_url_2')}
                          disabled={testing === 'forward_url_2' || !cfg.forward_url_2}
                          className="btn-ghost" style={{ fontSize:12, whiteSpace:'nowrap' }}>
                          {testing === 'forward_url_2' ? '⏳' : '🧪 Test'}
                        </button>
                      </div>
                      {testResult.forward_url_2 && (
                        <div style={{ marginTop:6, fontSize:11, padding:'6px 10px', borderRadius:6,
                          background: testResult.forward_url_2.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                          border:`1px solid ${testResult.forward_url_2.success ? '#10b981' : 'var(--danger)'}`,
                          color: testResult.forward_url_2.success ? '#10b981' : 'var(--danger)' }}>
                          {testResult.forward_url_2.success
                            ? `✅ Success — HTTP ${testResult.forward_url_2.status} in ${testResult.forward_url_2.ms}ms`
                            : `❌ Failed — ${testResult.forward_url_2.error}`}
                        </div>
                      )}
                      <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                        Sent alongside primary — useful for dual integrations (e.g. n8n + analytics)
                      </div>
                    </div>

                    {/* Signing Secret */}
                    <div style={{ marginBottom:16 }}>
                      <label className="label">Signing Secret <span style={{ fontWeight:400, color:'var(--text3)' }}>(optional)</span></label>
                      <div style={{ display:'flex', gap:8 }}>
                        <input type="text" value={cfg.forward_secret} placeholder="whsec_..."
                          onChange={e => setCfg(p => ({ ...p, forward_secret: e.target.value }))}
                          style={{ flex:1, fontFamily:'var(--mono)', fontSize:12 }} />
                        <button onClick={genSecret} className="btn-ghost" style={{ fontSize:12, whiteSpace:'nowrap' }}>
                          🔑 Generate
                        </button>
                      </div>
                      <div style={{ fontSize:11, color:'var(--text3)', marginTop:4, lineHeight:1.6 }}>
                        If set, requests include <code>X-WA-Signature: sha256=...</code> header.<br/>
                        Verify: <code>HMAC(secret, timestamp + '.' + body) === signature</code>
                      </div>
                    </div>

                    <button onClick={save} disabled={saving} className="btn-primary" style={{ fontSize:13 }}>
                      {saving ? 'Saving…' : '💾 Save Forward Settings'}
                    </button>
                  </>
                )}
              </div>

              {/* Payload format */}
              <div className="card">
                <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--accent)', marginBottom:8 }}>
                  // PAYLOAD FORMAT — what your endpoint receives
                </div>
                <pre style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8,
                  padding:'12px 14px', fontSize:11, fontFamily:'var(--mono)', overflow:'auto',
                  color:'var(--text2)', lineHeight:1.6, maxHeight:220, margin:0 }}>
{`{
  "_meta": {
    "source": "wa-dashboard",
    "event": "inbound",          // inbound | status | raw
    "wa_number_id": 1,
    "phone_number_id": "639242...",
    "number_label": "Main Number",
    "ts": 1718000000000
  },
  "metadata": {
    "phone_number_id": "...",
    "display_phone_number": "+91 86815 68465"
  },
  "messages": [{
    "id": "wamid.xxx",
    "from": "919876543210",
    "timestamp": "1718000000",
    "type": "text",
    "text": { "body": "Hello!" }
  }],
  "contacts": [{
    "profile": { "name": "John Doe" },
    "wa_id": "919876543210"
  }]
}`}
                </pre>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:10, lineHeight:1.7 }}>
                  ℹ️ For <strong>status</strong> events, <code>messages</code> is replaced by <code>statuses</code> array with delivery receipts.<br/>
                  Your endpoint must respond HTTP 2xx within 8 seconds. Failed forwards are logged, not retried.
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function fStyle(t) {
  return { padding:'10px 14px', borderRadius:'var(--r-sm)', fontSize:13, marginBottom:12,
    background: t==='ok' ? 'rgba(79,209,197,0.08)' : 'rgba(239,68,68,0.08)',
    border:`1px solid ${t==='ok' ? 'var(--accent)' : 'var(--danger)'}`,
    color: t==='ok' ? 'var(--accent)' : 'var(--danger)' };
}
